import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  FormControl,
  Pagination,
  Table,
} from "react-bootstrap";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import NotificationSystem from "react-notification-system";
import Select from "react-select";
import Swal from "sweetalert2";

import { GetAllDoctors } from "Listfunction";
import {
  downloadDoctorPeriodPayslip,
  exportDoctorConsultationPayments,
  fetchDoctorConsultationPayments,
  markConsultationPaymentsPaid,
  updateConsultationPaymentStatus,
} from "api/doctorConsultationPayments/doctorConsultationPayments";
import { getApiErrorMessage } from "utils/getApiErrorMessage";
import { style } from "variables/Variables";

import ConsultationTable, { StatusBadge } from "./ConsultationTable";
import {
  formatCount,
  formatCurrency,
  formatPercentage,
  getDateRangeLabel,
  getDefaultDateRange,
  getDoctorId,
  getPageNumbers,
  getPersonName,
  getValidationMessages,
  groupConsultationsByMonth,
} from "./paymentReportUtils";
import "./doctorConsultationPayments.css";

const RECORDS_PER_PAGE = 20;
const REVERSAL_REASON_MAX_LENGTH = 1000;

const escapeHtml = (value) =>
  String(value == null ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

// Pull the backend's field-level validation message for `reversal_reason`
// (returned on HTTP 422) so it can be surfaced inline in the popup.
const getReversalReasonError = (error) => {
  const responseData = (error && error.response && error.response.data) || {};
  const errors =
    responseData.errors ||
    (responseData.data && responseData.data.errors) ||
    {};
  const value = errors.reversal_reason;

  if (Array.isArray(value)) {
    return value.filter(Boolean).map(String)[0] || "";
  }

  return value ? String(value) : "";
};

const createDefaultFilters = () => {
  const dateRange = getDefaultDateRange();
  return {
    doctor: null,
    fromDate: dateRange.fromDate,
    paymentStatus: "",
    toDate: dateRange.toDate,
  };
};

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch (error) {
    return null;
  }
};

const getResponsePayload = (response) => {
  let payload = (response && response.data) || {};

  for (let depth = 0; depth < 3; depth += 1) {
    if (
      payload &&
      typeof payload === "object" &&
      !Array.isArray(payload) &&
      payload.records
    ) {
      return payload;
    }

    if (
      payload &&
      typeof payload === "object" &&
      !Array.isArray(payload) &&
      payload.data
    ) {
      payload = payload.data;
    } else {
      break;
    }
  }

  return payload || {};
};

const extractPaginatedRecords = (source) => {
  if (!source || typeof source !== "object") {
    return null;
  }

  if (Array.isArray(source)) {
    return {
      data: source,
      current_page: 1,
      from: source.length ? 1 : 0,
      last_page: 1,
      per_page: source.length || RECORDS_PER_PAGE,
      to: source.length,
      total: source.length,
    };
  }

  if (Array.isArray(source.data)) {
    return source;
  }

  return null;
};

const normalizeReportResponse = (response, fallbackPage) => {
  const report = getResponsePayload(response);

  let records = null;

  if (report.records && typeof report.records === "object") {
    records = extractPaginatedRecords(report.records);
  }

  if (!records && Array.isArray(report.data)) {
    records = extractPaginatedRecords(report);
  }

  if (!records && Array.isArray(report.records)) {
    records = extractPaginatedRecords(report.records);
  }

  if (!records) {
    records = {
      data: [],
      current_page: fallbackPage || 1,
      from: 0,
      last_page: 1,
      per_page: RECORDS_PER_PAGE,
      to: 0,
      total: 0,
    };
  }

  const doctorRecords = (Array.isArray(records.data) ? records.data : [])
    .filter((record) => record && typeof record === "object")
    .map((record) => ({
      ...record,
      consultations: (Array.isArray(record.consultations)
        ? record.consultations
        : []
      ).filter(
        (consultation) =>
          consultation && typeof consultation === "object",
      ),
    }));

  return {
    doctorRecords,
    summary:
      report.summary && typeof report.summary === "object"
        ? report.summary
        : {},
    pagination: {
      current_page: Number(records.current_page) || fallbackPage || 1,
      from: Number(records.from) || 0,
      last_page: Math.max(Number(records.last_page) || 1, 1),
      per_page: Number(records.per_page) || RECORDS_PER_PAGE,
      to: Number(records.to) || 0,
      total: Number(records.total) || 0,
    },
  };
};

const getPaymentServiceErrorMessage = (error) => {
  const message = getApiErrorMessage(error);
  const browserIsOnline =
    typeof navigator === "undefined" || navigator.onLine !== false;

  if (message === "Please Check internet" && browserIsOnline) {
    return "Unable to reach the consultation payments service. Please try again.";
  }

  return message;
};

const assertSuccessfulPaymentUpdate = (response) => {
  const responseData = (response && response.data) || {};

  if (responseData.success === false) {
    const apiError = new Error(
      responseData.message || "Payment status update failed.",
    );
    apiError.response = { data: responseData };
    throw apiError;
  }

  return response;
};

const parseFilenameFromContentDisposition = (header) => {
  if (typeof header !== "string" || !header) {
    return "";
  }

  const utf8Match = header.match(/filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i);
  if (utf8Match && utf8Match[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim().replace(/^"|"$/g, ""));
    } catch (error) {
      // fall through
    }
  }

  const match = header.match(/filename\s*=\s*"?([^";]+)"?/i);
  return match && match[1] ? match[1].trim() : "";
};

const readBlobAsText = (blob) =>
  new Promise((resolve) => {
    if (!blob || typeof blob.text !== "function") {
      if (blob && typeof FileReader !== "undefined") {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ""));
        reader.onerror = () => resolve("");
        reader.readAsText(blob);
        return;
      }
      resolve("");
      return;
    }

    blob
      .text()
      .then((text) => resolve(text || ""))
      .catch(() => resolve(""));
  });

const triggerBlobDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const ReportLoadingRows = () =>
  Array.from({ length: 5 }, (_, index) => (
    <tr key={`payment-loading-row-${index}`}>
      <td>
        <Skeleton circle width={30} height={30} />
      </td>
      <td>
        <Skeleton width={130} />
      </td>
      {Array.from({ length: 10 }, (__, cellIndex) => (
        <td key={`payment-loading-cell-${cellIndex}`}>
          <Skeleton width={cellIndex > 2 ? 92 : 62} />
        </td>
      ))}
    </tr>
  ));

class ConsultationPaymentsErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Consultation payments render error", error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="main-content consultation-payment-page">
          <div className="container-fluid">
            <div className="consultation-payment-render-error" role="alert">
              <i className="fa fa-exclamation-triangle" aria-hidden="true" />
              <h2>Unable to display the payment report</h2>
              <p>
                The report returned data that could not be displayed. Refresh the report and try the filters again.
              </p>
              <Button onClick={this.handleReset}>Refresh report</Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const DoctorConsultationPayments = (props) => {
  const notificationRef = useRef(null);
  const selectAllRef = useRef(null);
  const storedUserRef = useRef(getStoredUser());
  const storedUser = storedUserRef.current;

  const [filters, setFilters] = useState(createDefaultFilters);
  const [doctorOptions, setDoctorOptions] = useState([]);
  const [doctorsLoading, setDoctorsLoading] = useState(false);
  const [doctorRecords, setDoctorRecords] = useState([]);
  const [reportSummary, setReportSummary] = useState({});
  const [selectedPaymentIds, setSelectedPaymentIds] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    from: 0,
    last_page: 1,
    per_page: RECORDS_PER_PAGE,
    to: 0,
    total: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [reportLoading, setReportLoading] = useState(true);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [filterErrors, setFilterErrors] = useState([]);
  const [expandedDoctorIds, setExpandedDoctorIds] = useState([]);
  const [updatingPaymentIds, setUpdatingPaymentIds] = useState([]);
  const [bulkUpdatingDoctorIds, setBulkUpdatingDoctorIds] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [downloadingPayslip, setDownloadingPayslip] = useState("");

  const notify = useCallback((level, message) => {
    if (!notificationRef.current) {
      return;
    }

    notificationRef.current.addNotification({
      autoDismiss: level === "success" ? 6 : 10,
      level,
      message,
      position: "tr",
    });
  }, []);

  const handlePeriodPayslipDownload = async (doctorId, paymentIds, dateRange, key) => {
    const downloadKey = key || String(doctorId);
    setDownloadingPayslip(downloadKey);
    try {
      const response = await downloadDoctorPeriodPayslip(
        storedUser.token,
        doctorId,
        {
          from_date: dateRange.from_date,
          to_date: dateRange.to_date,
          payment_ids: paymentIds,
        },
      );
      triggerBlobDownload(
        response.data,
        `doctor-payslip-${doctorId}-${dateRange.from_date}-to-${dateRange.to_date}.pdf`,
      );
    } catch (error) {
      if (error && error.response && Number(error.response.status) === 401) {
        props.history.push("/auth/login-page");
      } else {
        const response = error && error.response;
        const responseData = response && response.data;
        let details = responseData;
        if (responseData instanceof Blob) {
          try {
            details = JSON.parse(await readBlobAsText(responseData));
          } catch (parseError) {
            details = null;
          }
        }
        const serverMessage = details && typeof details.message === "string"
          ? details.message
          : "";
        const status = response && response.status;
        notify(
          "error",
          serverMessage || (status === 404
            ? "Payslip download is unavailable on this API environment (HTTP 404). The API route needs to be deployed."
            : status
              ? `Unable to download this payslip (HTTP ${status}). Please report this error.`
              : "Unable to reach the payslip service. Please try again."),
        );
      }
    } finally {
      setDownloadingPayslip("");
    }
  };

  // ── Direct load function (like Lead Retention's loadProfiles) ──
  const loadReport = useCallback(
    (page = 1, activeFilters = null) => {
      if (!storedUser || !storedUser.token) {
        return;
      }

      const filtersToUse = activeFilters || filters;

      setReportLoading(true);
      setLoadError("");
      setFilterErrors([]);

      fetchDoctorConsultationPayments(storedUser.token, {
        doctor_id: filtersToUse.doctor ? filtersToUse.doctor.value : "",
        from_date: filtersToUse.fromDate,
        page,
        payment_status: filtersToUse.paymentStatus,
        records_per_page: RECORDS_PER_PAGE,
        to_date: filtersToUse.toDate,
      })
        .then((response) => {
          const normalizedReport = normalizeReportResponse(response, page);
          setDoctorRecords(normalizedReport.doctorRecords);
          setReportSummary(normalizedReport.summary);
          setSelectedPaymentIds([]);
          setPagination(normalizedReport.pagination);
          setInitialLoaded(true);
        })
        .catch((error) => {
          const validationMessages = getValidationMessages(error);
          if (
            error &&
            error.response &&
            Number(error.response.status) === 422 &&
            validationMessages.length
          ) {
            setFilterErrors(validationMessages);
            notify("error", validationMessages[0]);
          } else {
            const errorMessage = getPaymentServiceErrorMessage(error);
            setLoadError(errorMessage);
            notify("error", errorMessage);
          }
          setInitialLoaded(true);
        })
        .finally(() => {
          setReportLoading(false);
        });
    },
    [filters, notify, storedUser],
  );

  // ── Redirect if not logged in ──
  useEffect(() => {
    if (!storedUser || !storedUser.token) {
      props.history.push("/auth/login-page");
    }
  }, [props.history, storedUser]);

  // ── Load doctors dropdown ──
  useEffect(() => {
    if (!storedUser || !storedUser.token) {
      return undefined;
    }

    let active = true;
    setDoctorsLoading(true);

    GetAllDoctors(storedUser.token)
      .then((response) => {
        if (!active) {
          return;
        }

        // GetAllDoctors already unwraps axios' res.data, so the list may sit
        // at response.data or (paginated) at response.data.data.
        const payload = response && response.data;
        const doctors = Array.isArray(payload)
          ? payload
          : payload && Array.isArray(payload.data)
            ? payload.data
            : [];
        setDoctorOptions(
          doctors.map((doctor) => ({
            label: getPersonName(doctor, `Doctor #${doctor.id}`),
            value: doctor.id,
          })),
        );
      })
      .catch((error) => {
        if (active) {
          notify("error", getApiErrorMessage(error));
        }
      })
      .then(() => {
        if (active) {
          setDoctorsLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [notify, storedUser]);

  // ── Initial data load ──
  useEffect(() => {
    loadReport(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Filter handlers (following Lead Retention pattern) ──
  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    if (filters.fromDate && filters.toDate && filters.toDate < filters.fromDate) {
      setFilterErrors(["To date cannot be earlier than from date."]);
      return;
    }

    setCurrentPage(1);
    loadReport(1, filters);
  };

  const handleClearFilters = () => {
    const defaultFilters = createDefaultFilters();
    setFilters(defaultFilters);
    setCurrentPage(1);
    loadReport(1, defaultFilters);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    loadReport(page, filters);
  };

  const handleExport = async () => {
    if (!storedUser || !storedUser.token || exporting) {
      return;
    }

    if (filters.fromDate && filters.toDate && filters.toDate < filters.fromDate) {
      setFilterErrors(["To date cannot be earlier than from date."]);
      return;
    }

    setExporting(true);
    setFilterErrors([]);

    const exportFilters = {
      doctor_id: filters.doctor ? filters.doctor.value : "",
      doctor_name:
        filters.doctor && filters.doctor.label ? filters.doctor.label : "",
      from_date: filters.fromDate,
      payment_status: filters.paymentStatus,
      to_date: filters.toDate,
    };

    try {
      const response = await exportDoctorConsultationPayments(
        storedUser.token,
        exportFilters,
      );

      const headers = (response && response.headers) || {};
      const contentDisposition =
        headers["content-disposition"] || headers["Content-Disposition"] || "";
      const todayIso = new Date().toISOString().slice(0, 10);
      const filename =
        parseFilenameFromContentDisposition(contentDisposition) ||
        `doctor-consultation-payments-${todayIso}.xlsx`;

      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], {
              type:
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });

      triggerBlobDownload(blob, filename);
    } catch (error) {
      const status =
        error && error.response ? Number(error.response.status) : 0;

      if (status === 401) {
        props.history.push("/auth/login-page");
        return;
      }

      const errorBody = error && error.response ? error.response.data : null;
      let parsedBody = null;

      if (errorBody instanceof Blob) {
        const text = await readBlobAsText(errorBody);
        if (text) {
          try {
            parsedBody = JSON.parse(text);
          } catch (parseError) {
            parsedBody = { message: text };
          }
        }
      } else if (errorBody && typeof errorBody === "object") {
        parsedBody = errorBody;
      }

      const parsedError = parsedBody
        ? { response: { data: parsedBody, status } }
        : error;

      if (status === 400) {
        notify("error", "No records match the current filters.");
      } else if (status === 422) {
        const validationMessages = getValidationMessages(parsedError);
        if (validationMessages.length) {
          setFilterErrors(validationMessages);
          notify("error", validationMessages[0]);
        } else {
          notify("error", "Export failed, please try again.");
        }
      } else {
        notify("error", "Export failed, please try again.");
      }
    } finally {
      setExporting(false);
    }
  };

  const toggleDoctor = (doctorId) => {
    setExpandedDoctorIds((currentIds) =>
      currentIds.indexOf(doctorId) >= 0
        ? currentIds.filter((id) => id !== doctorId)
        : currentIds.concat(doctorId),
    );
  };

  const visiblePaymentIds = doctorRecords.reduce(
    (paymentIds, doctorRecord) =>
      paymentIds.concat(
        (Array.isArray(doctorRecord.consultations)
          ? doctorRecord.consultations
          : []
        )
          .filter((consultation) => consultation.payment_id)
          .map((consultation) => String(consultation.payment_id)),
      ),
    [],
  );
  const allVisibleSelected =
    visiblePaymentIds.length > 0 &&
    visiblePaymentIds.every(
      (paymentId) => selectedPaymentIds.indexOf(paymentId) >= 0,
    );
  const someVisibleSelected = visiblePaymentIds.some(
    (paymentId) => selectedPaymentIds.indexOf(paymentId) >= 0,
  );

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate =
        someVisibleSelected && !allVisibleSelected;
    }
  }, [allVisibleSelected, someVisibleSelected]);

  const toggleConsultationSelection = (paymentId) => {
    const normalizedPaymentId = String(paymentId);
    setSelectedPaymentIds((currentIds) =>
      currentIds.indexOf(normalizedPaymentId) >= 0
        ? currentIds.filter((id) => id !== normalizedPaymentId)
        : currentIds.concat(normalizedPaymentId),
    );
  };

  const toggleDoctorSelection = (consultations) => {
    const doctorPaymentIds = (Array.isArray(consultations) ? consultations : [])
      .filter((consultation) => consultation.payment_id)
      .map((consultation) => String(consultation.payment_id));
    const doctorFullySelected =
      doctorPaymentIds.length > 0 &&
      doctorPaymentIds.every(
        (paymentId) => selectedPaymentIds.indexOf(paymentId) >= 0,
      );

    setSelectedPaymentIds((currentIds) =>
      doctorFullySelected
        ? currentIds.filter((id) => doctorPaymentIds.indexOf(id) < 0)
        : currentIds.concat(
            doctorPaymentIds.filter((id) => currentIds.indexOf(id) < 0),
          ),
    );
  };

  const toggleAllVisibleConsultations = () => {
    setSelectedPaymentIds(allVisibleSelected ? [] : visiblePaymentIds);
  };

  const handleConsultationPaymentAction = async ({
    action,
    consultation,
    doctorName,
  }) => {
    if (!consultation || !consultation.payment_id) {
      return;
    }

    // Reverting a PAID consultation back to UNPAID requires a reason; collect
    // it through the popup before hitting the API.
    if (action === "unpaid") {
      openReversalPrompt({
        mode: "single",
        doctorName,
        targets: [
          {
            payment_id: consultation.payment_id,
            name: getPersonName(consultation.patient),
          },
        ],
      });
      return;
    }

    const paymentId = String(consultation.payment_id);
    setUpdatingPaymentIds((currentIds) =>
      currentIds.indexOf(paymentId) >= 0
        ? currentIds
        : currentIds.concat(paymentId),
    );

    try {
      const response = await updateConsultationPaymentStatus(
        storedUser.token,
        consultation.payment_id,
        { status: action },
      );
      assertSuccessfulPaymentUpdate(response);

      notify(
        "success",
        `${doctorName}'s consultation marked as ${action}.`,
      );
      loadReport(currentPage, filters);
    } catch (error) {
      const validationMessages = getValidationMessages(error);
      const message =
        validationMessages[0] || getPaymentServiceErrorMessage(error);
      notify("error", message);
    } finally {
      setUpdatingPaymentIds((currentIds) =>
        currentIds.filter((id) => id !== paymentId),
      );
    }
  };

  const handleDoctorBulkPaymentAction = async ({
    consultations,
    doctorId,
    doctorName,
    status,
  }) => {
    const eligibleConsultations = (Array.isArray(consultations)
      ? consultations
      : []
    ).filter(
      (consultation) =>
        consultation.payment_id &&
        String(consultation.payment_status || "").toLowerCase() !== status,
    );

    if (!eligibleConsultations.length) {
      notify("info", `All filtered consultations for ${doctorName} are already ${status}.`);
      return;
    }

    // Bulk revert to UNPAID also needs a shared reversal reason; open the popup
    // and defer the API calls until the user confirms.
    if (status === "unpaid") {
      openReversalPrompt({
        mode: "bulk",
        doctorName,
        targets: eligibleConsultations.map((consultation) => ({
          payment_id: consultation.payment_id,
          name: getPersonName(consultation.patient),
        })),
      });
      return;
    }

    setBulkUpdatingDoctorIds((currentIds) =>
      currentIds.indexOf(doctorId) >= 0
        ? currentIds
        : currentIds.concat(doctorId),
    );

    try {
      const response = await markConsultationPaymentsPaid(
        storedUser.token,
        eligibleConsultations.map((consultation) => consultation.payment_id),
      );
      assertSuccessfulPaymentUpdate(response);
      const result = (response.data && response.data.data) || {};
      const paid = Array.isArray(result.paid) ? result.paid : [];
      const failed = Array.isArray(result.failed) ? result.failed : [];
      const skipped = Array.isArray(result.skipped_payment_ids)
        ? result.skipped_payment_ids
        : [];
      const successCount = paid.reduce(
        (count, payout) => count + (Number(payout.consultation_count) || 0),
        0,
      );

      loadReport(currentPage, filters);

      if (failed.length) {
        const failedCount = failed.reduce(
          (count, payout) => count + (Array.isArray(payout.payment_ids) ? payout.payment_ids.length : 0),
          0,
        );
        notify(
          "error",
          `${successCount} consultation${successCount === 1 ? "" : "s"} updated; ${failedCount} failed. ${failed[0].message || "Refresh the report and try again."}`,
        );
      } else {
        notify(
          "success",
          `${successCount} filtered consultation${successCount === 1 ? "" : "s"} for ${doctorName} marked as ${status}.${skipped.length ? ` ${skipped.length} already paid.` : ""}`,
        );
      }
    } catch (error) {
      // A gateway error gives no reliable result; the server may have committed.
      loadReport(currentPage, filters);
      const failures = error && error.response && error.response.data &&
        error.response.data.data && error.response.data.data.failed;
      const validationMessages = getValidationMessages(error);
      notify(
        "error",
        `${(Array.isArray(failures) && failures[0] && failures[0].message) || validationMessages[0] || getPaymentServiceErrorMessage(error)} Refresh the report before retrying.`,
      );
    } finally {
      setBulkUpdatingDoctorIds((currentIds) =>
        currentIds.filter((id) => id !== doctorId),
      );
    }
  };

  const handleSelectedConsultationsPaymentAction = (status) => {
    const consultations = doctorRecords.reduce(
      (allConsultations, doctorRecord) =>
        allConsultations.concat(
          Array.isArray(doctorRecord.consultations)
            ? doctorRecord.consultations.filter(
                (consultation) =>
                  selectedPaymentIds.indexOf(String(consultation.payment_id)) >= 0,
              )
            : [],
        ),
      [],
    );

    handleDoctorBulkPaymentAction({
      consultations,
      doctorId: selectedPaymentIds.join(","),
      doctorName: `${selectedPaymentIds.length} selected consultation${
        selectedPaymentIds.length === 1 ? "" : "s"
      }`,
      status,
    });
  };

  // Reason popup for reverting PAID → UNPAID. Uses SweetAlert2 to match the
  // confirmation pattern used elsewhere in the admin (e.g. claim approval).
  const openReversalPrompt = ({ mode, doctorName, targets }) => {
    const eligibleTargets = Array.isArray(targets) ? targets : [];
    if (!eligibleTargets.length) {
      return;
    }

    const isBulk = mode === "bulk";
    const safeDoctorName = escapeHtml(doctorName || "this doctor");
    const countLine = isBulk
      ? `<p class="consultation-reversal__count">This will revert <strong>${eligibleTargets.length}</strong> paid consultation${
          eligibleTargets.length === 1 ? "" : "s"
        } for ${safeDoctorName}.</p>`
      : "";

    // Targets still awaiting a successful revert. Retries operate on this set so
    // only the payments that failed are resent, keeping the same reason.
    let pendingTargets = eligibleTargets;
    let totalSucceeded = 0;

    Swal.fire({
      width: "500px",
      title: "<strong><font size='36px'>Revert Payment to Unpaid</font></strong>",
      icon: "warning",
      html: `
        <div class="consultation-reversal">
          <p class="consultation-reversal__warning">
            Reverting to unpaid reverses the doctor's wallet debit for ${
              isBulk ? "these consultations" : "this consultation"
            }. A balancing credit is created to undo the original payment.
          </p>
          ${countLine}
          <label for="consultation-reversal-reason" class="consultation-reversal__label">
            Reason for reverting
          </label>
          <textarea
            id="consultation-reversal-reason"
            class="consultation-reversal__textarea"
            placeholder="Explain why this payment is being reverted"
            rows="4"
            maxlength="${REVERSAL_REASON_MAX_LENGTH}"
          ></textarea>
          <div class="consultation-reversal__counter">
            <span id="consultation-reversal-count">0</span>/${REVERSAL_REASON_MAX_LENGTH}
          </div>
        </div>
      `,
      showCloseButton: true,
      showCancelButton: true,
      focusConfirm: false,
      confirmButtonText: "<font size='4px'>Confirm Revert</font>",
      cancelButtonText: "<font size='4px'>Cancel</font>",
      customClass: {
        popup: "consultation-reversal__popup",
        confirmButton: "consultation-reversal__confirm",
        cancelButton: "consultation-reversal__cancel",
      },
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      didOpen: () => {
        const popup = Swal.getPopup();
        const textarea = document.getElementById("consultation-reversal-reason");
        const counter = document.getElementById("consultation-reversal-count");
        const confirmButton = Swal.getConfirmButton();

        if (popup) {
          popup.addEventListener("focusin", (event) => {
            event.stopPropagation();
          });
        }

        // Keep "Confirm Revert" disabled until a non-empty (trimmed) reason
        // is entered, and keep the live character counter in sync.
        const syncState = () => {
          const value = textarea ? textarea.value : "";
          if (counter) {
            counter.textContent = String(value.length);
          }
          if (confirmButton) {
            confirmButton.disabled = !value.trim();
          }
        };

        if (confirmButton) {
          confirmButton.disabled = true;
        }
        if (textarea) {
          textarea.addEventListener("input", syncState);
          textarea.focus();
        }
      },
      preConfirm: async () => {
        const textarea = document.getElementById("consultation-reversal-reason");
        const reason = (textarea ? textarea.value : "").trim();

        if (!reason) {
          Swal.showValidationMessage("Reason for reverting is required");
          return false;
        }

        const results = await Promise.all(
          pendingTargets.map((target) =>
            updateConsultationPaymentStatus(storedUser.token, target.payment_id, {
              status: "unpaid",
              reversal_reason: reason,
            })
              .then(assertSuccessfulPaymentUpdate)
              .then(() => ({ success: true, target }))
              .catch((error) => ({ success: false, target, error })),
          ),
        );

        const failed = results.filter((result) => !result.success);
        const succeeded = results.length - failed.length;

        // Any success means the report + aggregate totals are stale; refresh
        // in place (no full page reload).
        if (succeeded > 0) {
          totalSucceeded += succeeded;
          loadReport(currentPage, filters);
        }

        if (failed.length) {
          // Keep the popup open and retry only the failures on the next click.
          pendingTargets = failed.map((result) => result.target);
          const firstError = failed[0].error;
          const message =
            getReversalReasonError(firstError) ||
            getValidationMessages(firstError)[0] ||
            getPaymentServiceErrorMessage(firstError);

          Swal.showValidationMessage(
            isBulk
              ? `${succeeded} reverted, ${failed.length} failed. ${message} Press Confirm Revert to retry the ${failed.length} that failed.`
              : message,
          );
          return false;
        }

        return { totalSucceeded };
      },
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        const revertedCount = result.value.totalSucceeded;
        notify(
          "success",
          isBulk
            ? `${revertedCount} consultation${
                revertedCount === 1 ? "" : "s"
              } for ${doctorName} reverted to unpaid.`
            : `${doctorName}'s consultation reverted to unpaid.`,
        );
      }
    });
  };

  const isInitialLoading = reportLoading && !initialLoaded;
  const isFilterLoading = reportLoading && initialLoaded;
  const currentReportPage = Number(pagination.current_page) || currentPage;
  const lastPage = Number(pagination.last_page) || 1;
  const pageNumbers = getPageNumbers(currentReportPage, lastPage);

  return (
    <div className="main-content consultation-payment-page">
      <NotificationSystem ref={notificationRef} style={style} />
      <div className="container-fluid">
        <div className="consultation-payment-shell">
          <header className="consultation-payment-masthead">
            <div className="consultation-payment-masthead__accent" />
            <div>
              <h1>Doctor Consultation Payment</h1>
              <p>
                Review doctor earnings and update consultation payment status. All totals reflect only the selected date range.
              </p>
            </div>
          </header>

          <section className="consultation-payment-filters" aria-labelledby="payment-filter-title">
            <div className="consultation-payment-section-heading">
              <div>
                <h2 id="payment-filter-title">Filters</h2>
                <p>Narrow the report by doctor, date range, or overall doctor status.</p>
              </div>
              {isFilterLoading ? (
                <span className="consultation-payment-loading-label" role="status">
                  <i className="fa fa-spinner fa-spin" aria-hidden="true" /> Updating report…
                </span>
              ) : null}
            </div>

            {filterErrors.length ? (
              <Alert bsStyle="danger" className="consultation-payment-filter-alert">
                <ul>
                  {filterErrors.map((message, index) => (
                    <li key={`${message}-${index}`}>{message}</li>
                  ))}
                </ul>
              </Alert>
            ) : null}

            <div className="consultation-payment-filter-grid">
              <div className="consultation-payment-field consultation-payment-field--doctor">
                <label htmlFor="consultation-payment-doctor">Doctor</label>
                <Select
                  inputId="consultation-payment-doctor"
                  value={filters.doctor}
                  options={doctorOptions}
                  onChange={(doctor) =>
                    updateFilter("doctor", doctor)
                  }
                  isClearable
                  isLoading={doctorsLoading}
                  placeholder="All doctors"
                  classNamePrefix="consultation-payment-select"
                  // The filter card clips overflow, so render the menu on body.
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 1050 }) }}
                />
              </div>
              <div className="consultation-payment-field">
                <label htmlFor="consultation-payment-from-date">From date</label>
                <FormControl
                  id="consultation-payment-from-date"
                  type="date"
                  value={filters.fromDate}
                  onChange={(event) =>
                    updateFilter("fromDate", event.target.value)
                  }
                />
              </div>
              <div className="consultation-payment-field">
                <label htmlFor="consultation-payment-to-date">To date</label>
                <FormControl
                  id="consultation-payment-to-date"
                  type="date"
                  value={filters.toDate}
                  onChange={(event) =>
                    updateFilter("toDate", event.target.value)
                  }
                />
              </div>
              <div className="consultation-payment-field">
                <label htmlFor="consultation-payment-status">Doctor payment status</label>
                <FormControl
                  id="consultation-payment-status"
                  componentClass="select"
                  value={filters.paymentStatus}
                  onChange={(event) =>
                    updateFilter("paymentStatus", event.target.value)
                  }
                >
                  <option value="">All</option>
                  <option value="paid">Paid</option>
                  <option value="unpaid">Unpaid</option>
                </FormControl>
              </div>
              <div className="consultation-payment-filter-actions">
                <Button
                  className="consultation-payment-button consultation-payment-button--primary"
                  onClick={handleApplyFilters}
                  disabled={reportLoading}
                >
                  {isFilterLoading ? (
                    <i className="fa fa-spinner fa-spin" aria-hidden="true" />
                  ) : (
                    <i className="fa fa-filter" aria-hidden="true" />
                  )}
                  Apply filters
                </Button>
                <Button
                  className="consultation-payment-button consultation-payment-button--quiet"
                  onClick={handleClearFilters}
                  disabled={reportLoading}
                >
                  <i className="fa fa-undo" aria-hidden="true" />
                  Clear filters
                </Button>
                <Button
                  className="consultation-payment-button consultation-payment-button--export"
                  onClick={handleExport}
                  disabled={exporting || reportLoading}
                >
                  {exporting ? (
                    <>
                      <i className="fa fa-spinner fa-spin" aria-hidden="true" />
                      Exporting…
                    </>
                  ) : (
                    <>
                      <i className="fa fa-file-excel-o" aria-hidden="true" />
                      Export to Excel
                    </>
                  )}
                </Button>
              </div>
            </div>
          </section>

          <section className="consultation-payment-report" aria-labelledby="payment-report-title">
            <div className="consultation-payment-section-heading consultation-payment-report__heading">
              <div>
                <h2 id="payment-report-title">Doctor payment report</h2>
                <p>
                  Expand a doctor to review each logical consultation and its payment status.
                </p>
              </div>
              <div className="consultation-payment-range-summary">
                <div className="consultation-payment-range-summary__total">
                  <span>Net payment for selected date range</span>
                  <strong>{formatCurrency(reportSummary.doctor_net_payment)}</strong>
                </div>
                <div className="consultation-payment-range-summary__page">
                  <span>Showing</span>
                  <strong>
                    {formatCount(pagination.from)}–{formatCount(pagination.to)}
                  </strong>
                  <span>of {formatCount(pagination.total)}</span>
                </div>
              </div>
            </div>

            {selectedPaymentIds.length ? (
              <div className="consultation-payment-selection-bar" role="region" aria-label="Bulk payment actions">
                <strong>
                  {selectedPaymentIds.length} consultation{selectedPaymentIds.length === 1 ? "" : "s"} selected
                </strong>
                <div className="consultation-payment-selection-bar__actions">
                  <Button
                    bsStyle="success"
                    className="consultation-payment-action-button btn-fill"
                    disabled={bulkUpdatingDoctorIds.length > 0}
                    onClick={() => handleSelectedConsultationsPaymentAction("paid")}
                  >
                    Mark selected as Paid
                  </Button>
                  <Button
                    bsStyle="warning"
                    className="consultation-payment-action-button btn-fill"
                    disabled={bulkUpdatingDoctorIds.length > 0}
                    onClick={() => handleSelectedConsultationsPaymentAction("unpaid")}
                  >
                    Mark selected as Unpaid
                  </Button>
                  <Button
                    className="consultation-payment-selection-bar__clear"
                    disabled={bulkUpdatingDoctorIds.length > 0}
                    onClick={() => setSelectedPaymentIds([])}
                  >
                    Clear selection
                  </Button>
                </div>
              </div>
            ) : null}

            {loadError ? (
              <div className="consultation-payment-error-state" role="alert">
                <i className="fa fa-wifi" aria-hidden="true" />
                <h3>Unable to load the payment report</h3>
                <p>{loadError}</p>
                <Button onClick={() => loadReport(currentPage, filters)}>
                  <i className="fa fa-refresh" aria-hidden="true" /> Retry
                </Button>
              </div>
            ) : (
              <div className="consultation-payment-table-wrap">
                <Table className="consultation-payment-table">
                  <thead>
                    <tr>
                      <th aria-label="Expand doctor details" />
                      <th className="consultation-payment-table__select-cell">
                        <input
                          ref={selectAllRef}
                          type="checkbox"
                          checked={allVisibleSelected}
                          onChange={toggleAllVisibleConsultations}
                          disabled={!doctorRecords.length || reportLoading}
                          aria-label="Select all consultations on this page"
                        />
                      </th>
                      <th>Doctor</th>
                      <th>Selected date range</th>
                      <th>Logical consultations</th>
                      <th>Call attempts</th>
                      <th>Gross fees</th>

                      <th>Net payment</th>
                      <th>Paid consultations</th>
                      <th>Unpaid consultations</th>
                      <th>Overall status</th>
                      <th>Payment action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isInitialLoading ? (
                      <ReportLoadingRows />
                    ) : doctorRecords.length === 0 ? (
                      <tr>
                        <td colSpan={12} className="consultation-payment-state-cell">
                          <div className="consultation-payment-empty-state">
                            <i className="fa fa-calendar-times-o" aria-hidden="true" />
                            <h3>No payment records found</h3>
                            <p>
                              No doctors matched the selected date range and payment filters.
                            </p>
                            <Button onClick={handleClearFilters}>Clear filters</Button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      doctorRecords.map((doctorRecord, index) => {
                        const doctorId = getDoctorId(doctorRecord, index);
                        const doctorName = getPersonName(doctorRecord.doctor, "Unknown doctor");
                        const isExpanded = expandedDoctorIds.indexOf(doctorId) >= 0;
                        const detailRegionId = `doctor-consultations-${doctorId.replace(/[^a-zA-Z0-9-_]/g, "-")}`;
                        const consultations = Array.isArray(doctorRecord.consultations)
                          ? doctorRecord.consultations
                          : [];
                        const paidPaymentIds = Array.from(new Set(
                          consultations
                            .filter((consultation) =>
                              consultation.payment_id &&
                              String(consultation.payment_status || "").toLowerCase() === "paid",
                            )
                            .map((consultation) => Number(consultation.payment_id)),
                        ));
                        const doctorPaymentIds = consultations
                          .filter((consultation) => consultation.payment_id)
                          .map((consultation) => String(consultation.payment_id));
                        const isDoctorSelected =
                          doctorPaymentIds.length > 0 &&
                          doctorPaymentIds.every(
                            (paymentId) => selectedPaymentIds.indexOf(paymentId) >= 0,
                          );
                        const isDoctorPartiallySelected =
                          !isDoctorSelected &&
                          doctorPaymentIds.some(
                            (paymentId) => selectedPaymentIds.indexOf(paymentId) >= 0,
                          );
                        const bulkTargetStatus =
                          String(doctorRecord.payment_status || "").toLowerCase() === "paid"
                            ? "unpaid"
                            : "paid";
                        const bulkEligibleCount = consultations.filter(
                          (consultation) =>
                            consultation.payment_id &&
                            String(consultation.payment_status || "").toLowerCase() !==
                              bulkTargetStatus,
                        ).length;
                        const isBulkUpdating =
                          bulkUpdatingDoctorIds.indexOf(doctorId) >= 0;
                        const months = groupConsultationsByMonth(
                          consultations,
                          doctorRecord.date_range || { from_date: filters.fromDate, to_date: filters.toDate },
                        );
                        const isMultiMonth = months.length > 1;

                        return (
                          <React.Fragment key={doctorId}>
                            <tr className={isExpanded ? "consultation-payment-table__row--expanded" : ""}>
                              <td className="consultation-payment-table__expand-cell">
                                <Button
                                  className="consultation-payment-expand-button"
                                  onClick={() => toggleDoctor(doctorId)}
                                  aria-expanded={isExpanded}
                                  aria-controls={detailRegionId}
                                  aria-label={`${isExpanded ? "Collapse" : "Expand"} consultations for ${doctorName}`}
                                >
                                  <i
                                    className={`fa fa-chevron-${isExpanded ? "up" : "down"}`}
                                    aria-hidden="true"
                                  />
                                </Button>
                              </td>
                              <td className="consultation-payment-table__select-cell">
                                <input
                                  ref={(input) => {
                                    if (input) {
                                      input.indeterminate = isDoctorPartiallySelected;
                                    }
                                  }}
                                  type="checkbox"
                                  checked={isDoctorSelected}
                                  onChange={() => toggleDoctorSelection(consultations)}
                                  disabled={!doctorPaymentIds.length || isBulkUpdating}
                                  aria-label={`Select all consultations for ${doctorName}`}
                                />
                              </td>
                              <td className="consultation-payment-table__doctor-cell">
                                <strong>{doctorName}</strong>
                              </td>
                              <td>{getDateRangeLabel(doctorRecord.date_range, filters)}</td>
                              <td>{formatCount(doctorRecord.logical_call_count)}</td>
                              <td>{formatCount(doctorRecord.raw_call_attempt_count)}</td>
                              <td>{formatCurrency(doctorRecord.gross_fees)}</td>

                              <td>
                                <strong>{formatCurrency(doctorRecord.doctor_net_payment)}</strong>
                              </td>
                              <td>{formatCount(doctorRecord.paid_consultation_count)}</td>
                              <td>{formatCount(doctorRecord.unpaid_consultation_count)}</td>
                              <td>
                                <StatusBadge status={doctorRecord.payment_status} />
                              </td>
                              <td>
                                <Button
                                  bsStyle={bulkTargetStatus === "paid" ? "success" : "warning"}
                                  bsSize="small"
                                  className="consultation-payment-action-button consultation-payment-bulk-button btn-fill"
                                  disabled={!bulkEligibleCount || isBulkUpdating}
                                  onClick={() =>
                                    handleDoctorBulkPaymentAction({
                                      consultations,
                                      doctorId,
                                      doctorName,
                                      status: bulkTargetStatus,
                                    })
                                  }
                                >
                                  {isBulkUpdating ? (
                                    <span>
                                      <i className="fa fa-spinner fa-spin" aria-hidden="true" /> Updating…
                                    </span>
                                  ) : bulkTargetStatus === "paid" ? (
                                    `Mark all as Paid (${bulkEligibleCount})`
                                  ) : (
                                    `Mark all as Unpaid (${bulkEligibleCount})`
                                  )}
                                </Button>
                                {isMultiMonth ? (
                                  <Button
                                    bsSize="small"
                                    className="consultation-payment-payslip-link"
                                    onClick={() => {
                                      if (!isExpanded) {
                                        toggleDoctor(doctorId);
                                      }
                                    }}
                                    aria-controls={detailRegionId}
                                    aria-label={`Show monthly payslips for ${doctorName}`}
                                  >
                                    <i className="fa fa-calendar" aria-hidden="true" />{" "}
                                    Payslips by month ({months.length})
                                  </Button>
                                ) : paidPaymentIds.length ? (
                                  <Button
                                    bsSize="small"
                                    className="consultation-payment-payslip-link"
                                    disabled={Boolean(downloadingPayslip)}
                                    onClick={() => handlePeriodPayslipDownload(doctorRecord.doctor.id, paidPaymentIds, doctorRecord.date_range)}
                                    aria-label={`Download payslip for ${doctorName} in the selected date range`}
                                  >
                                    <i className={`fa fa-${downloadingPayslip === String(doctorRecord.doctor.id) ? "spinner fa-spin" : "download"}`} aria-hidden="true" />{" "}
                                    Download payslip
                                  </Button>
                                ) : null}
                              </td>
                            </tr>
                            {isExpanded ? (
                              <tr className="consultation-payment-detail-row">
                                <td colSpan={12} id={detailRegionId}>
                                  <div className="consultation-payment-detail-panel">
                                    <div className="consultation-payment-detail-panel__heading">
                                      <div>
                                        <h3>{doctorName} consultations</h3>
                                        <p>{getDateRangeLabel(doctorRecord.date_range, filters)}</p>
                                      </div>
                                      <span>
                                        {formatCount(doctorRecord.logical_call_count)} logical consultations
                                      </span>
                                    </div>
                                    {months.map((month) => {
                                      const monthKey = `${doctorId}:${month.key}`;
                                      const isMonthUpdating =
                                        isBulkUpdating ||
                                        bulkUpdatingDoctorIds.indexOf(monthKey) >= 0;
                                      const isMonthDownloading = downloadingPayslip === monthKey;

                                      return (
                                        <section
                                          key={month.key}
                                          className="consultation-payment-month"
                                          aria-label={`${doctorName} consultations in ${month.label}`}
                                        >
                                          <header className="consultation-payment-month__header">
                                            <div className="consultation-payment-month__title">
                                              <h4>{month.label}</h4>
                                              <p>
                                                {getDateRangeLabel(month.dateRange)} ·{" "}
                                                {formatCount(month.consultations.length)} consultations ·{" "}
                                                {formatCount(month.paidCount)} paid ·{" "}
                                                {formatCount(month.unpaidCount)} unpaid
                                              </p>
                                            </div>
                                            <div className="consultation-payment-month__totals">
                                              <span>Gross {formatCurrency(month.grossFees)}</span>
                                              <strong>Net {formatCurrency(month.netPayment)}</strong>
                                            </div>
                                            <div className="consultation-payment-month__actions">
                                              {month.unpaidCount > 0 ? (
                                                <Button
                                                  bsStyle="success"
                                                  bsSize="small"
                                                  className="consultation-payment-action-button btn-fill"
                                                  disabled={isMonthUpdating}
                                                  onClick={() =>
                                                    handleDoctorBulkPaymentAction({
                                                      consultations: month.consultations,
                                                      doctorId: monthKey,
                                                      doctorName: `${doctorName} (${month.label})`,
                                                      status: "paid",
                                                    })
                                                  }
                                                >
                                                  {isMonthUpdating ? (
                                                    <span>
                                                      <i className="fa fa-spinner fa-spin" aria-hidden="true" /> Updating…
                                                    </span>
                                                  ) : (
                                                    `Mark ${month.label} as Paid (${month.unpaidCount})`
                                                  )}
                                                </Button>
                                              ) : null}
                                              {month.paidPaymentIds.length ? (
                                                <Button
                                                  bsSize="small"
                                                  className="consultation-payment-payslip-link"
                                                  disabled={Boolean(downloadingPayslip)}
                                                  onClick={() =>
                                                    handlePeriodPayslipDownload(
                                                      doctorRecord.doctor.id,
                                                      month.paidPaymentIds,
                                                      month.dateRange,
                                                      monthKey,
                                                    )
                                                  }
                                                  aria-label={`Download ${month.label} payslip for ${doctorName}`}
                                                >
                                                  <i className={`fa fa-${isMonthDownloading ? "spinner fa-spin" : "download"}`} aria-hidden="true" />{" "}
                                                  {month.label} payslip
                                                </Button>
                                              ) : (
                                                <span className="consultation-payment-month__hint">
                                                  Payslip available once paid
                                                </span>
                                              )}
                                            </div>
                                          </header>
                                          <ConsultationTable
                                            bulkUpdating={isMonthUpdating}
                                            consultations={month.consultations}
                                            doctorName={doctorName}
                                            onPaymentAction={handleConsultationPaymentAction}
                                            onSelectionChange={toggleConsultationSelection}
                                            selectedPaymentIds={selectedPaymentIds}
                                            updatingPaymentIds={updatingPaymentIds}
                                          />
                                        </section>
                                      );
                                    })}
                                  </div>
                                </td>
                              </tr>
                            ) : null}
                          </React.Fragment>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </div>
            )}

            {!loadError && !isInitialLoading ? (
              <div className="consultation-payment-pagination-bar">
                <span>
                  Page {currentReportPage} of {lastPage}
                </span>
                <Pagination className="consultation-payment-pagination">
                  <Pagination.First
                    onClick={() => handlePageChange(1)}
                    disabled={currentReportPage <= 1 || reportLoading}
                  />
                  <Pagination.Prev
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                    disabled={currentReportPage <= 1 || reportLoading}
                  />
                  {pageNumbers[0] > 1 ? <Pagination.Ellipsis disabled /> : null}
                  {pageNumbers.map((pageNumber) => (
                    <Pagination.Item
                      key={pageNumber}
                      active={pageNumber === currentReportPage}
                      onClick={() => handlePageChange(pageNumber)}
                      disabled={reportLoading}
                    >
                      {pageNumber}
                    </Pagination.Item>
                  ))}
                  {pageNumbers[pageNumbers.length - 1] < lastPage ? (
                    <Pagination.Ellipsis disabled />
                  ) : null}
                  <Pagination.Next
                    onClick={() => handlePageChange(Math.min(lastPage, currentPage + 1))}
                    disabled={currentReportPage >= lastPage || reportLoading}
                  />
                  <Pagination.Last
                    onClick={() => handlePageChange(lastPage)}
                    disabled={currentReportPage >= lastPage || reportLoading}
                  />
                </Pagination>
              </div>
            ) : null}
          </section>
        </div>
      </div>
    </div>
  );
};

const DoctorConsultationPaymentsPage = (props) => (
  <ConsultationPaymentsErrorBoundary>
    <DoctorConsultationPayments {...props} />
  </ConsultationPaymentsErrorBoundary>
);

export default DoctorConsultationPaymentsPage;
